import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { MdBusiness, MdEventNote, MdGroups, MdMedicalServices, MdPerson } from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getClaimStatusRecord, postBulkClaimStatus, postClaimStatus } from '../../Services/Claim360Service';
import { OrganizationSelect, PayerSelect, ProviderSelect, usePayers } from '../../Components/Claim360/StediSelects';
import { BulkSubscriberImport, DateInput, FormSection, StediFormShell, TextInput } from '../../Components/Claim360/StediFormParts';
import ClaimStatusResultModal from '../../Components/Claim360/ClaimStatusResultModal';
import { insuranceMappings, splitPatientName } from '../../Utils/stediUtils';
import { toApiDate } from '../../Utils/DateTime';

const emptyForm = {
  organizationId: '',
  firstName: '',
  lastName: '',
  tradingPartnerServiceId: '',
  memberId: '',
  dob: '',
  beginningDateOfService: '',
  endDateOfService: '',
  providerId: '',
  procedureCode: '',
  lineChargeAmount: '',
};

const validationSchema = Yup.object({
  organizationId: Yup.string().required('Organization is required'),
  providerId: Yup.string().required('Provider is required'),
  beginningDateOfService: Yup.string().required('Beginning date of service is required'),
  endDateOfService: Yup.string().required('End date of service is required'),
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  tradingPartnerServiceId: Yup.string().trim().required('Payer is required'),
  memberId: Yup.string().trim().required('Member ID is required'),
  dob: Yup.string().required('Date of birth is required'),
  procedureCode: Yup.string().trim().max(48, 'Procedure code is too long'),
  lineChargeAmount: Yup.string().trim()
    .matches(/^\$?\d[\d,]*(\.\d{1,2})?$/, 'Enter an amount such as 150.00')
    .when('procedureCode', {
      is: (code) => !code?.trim(),
      then: (schema) => schema.test('needs-code', 'Enter the procedure code this charge is for', (value) => !value?.trim()),
    }),
});

// In bulk, the billing provider comes from each row, and dates of service are defaults for rows without their
// own, so they are checked against the rows.
const bulkValidationSchema = Yup.object({
  organizationId: Yup.string().required('Organization is required'),
});

const TABS = [
  { id: 'single', label: 'Single Check' },
  { id: 'bulk', label: 'Bulk Upload' },
];

// 276 body in Stedi's field names (what /stedi/claim-status validates). The billing provider is not sent:
// the API fills it from the selected organization (its name) and provider (its NPI).
// procedureCode / lineChargeAmount are not sent to the payer: they pick that service line out of its answer.
function buildPayload(values) {
  const procedureCode = values.procedureCode.trim();
  const lineChargeAmount = values.lineChargeAmount.trim().replace(/[$,]/g, '');
  return {
    tradingPartnerServiceId: values.tradingPartnerServiceId.trim(),
    subscriber: {
      memberId: values.memberId.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      dateOfBirth: values.dob,
    },
    encounter: {
      beginningDateOfService: toApiDate(values.beginningDateOfService),
      endDateOfService: toApiDate(values.endDateOfService),
    },
    ...(procedureCode && { procedureCode }),
    ...(procedureCode && lineChargeAmount && { lineChargeAmount }),
  };
}

// Multipart body of /stedi/bulk/claim-status: the uploaded sheet plus the defaults for rows without their own.
function buildBulkForm(values, file, rows) {
  const form = new FormData();
  form.append('file', file);
  form.append('insuranceMappings', JSON.stringify(insuranceMappings(rows)));
  if (values.tradingPartnerServiceId) form.append('payerId', values.tradingPartnerServiceId.trim());
  if (values.beginningDateOfService) form.append('serviceDateFrom', toApiDate(values.beginningDateOfService));
  if (values.endDateOfService) form.append('serviceDateTo', toApiDate(values.endDateOfService));
  return form;
}

function mapRecordToForm(record) {
  const { firstName, lastName } = splitPatientName(record?.patientName);
  return {
    ...emptyForm,
    organizationId: record?.organizationId ?? '',
    firstName,
    lastName,
    tradingPartnerServiceId: record?.payerId ?? '',
    memberId: record?.memberId ?? '',
    dob: record?.dateOfBirth ?? '',
    procedureCode: record?.procedureCode ?? '',
    lineChargeAmount: record?.lineChargeAmount ?? '',
  };
}

export default function ClaimStatusForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canSave = permissionHas(
    userPermissionCodes,
    isEdit ? PermissionCodes.CLAIM_STATUS_EDIT : PermissionCodes.CLAIM_STATUS_CREATE,
    decodedToken,
  );

  const [loading, setLoading] = useState(isEdit);
  const [activeTab, setActiveTab] = useState('single');
  const [bulkSubscribers, setBulkSubscribers] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [payers, payersLoading] = usePayers(token);
  // Result of the last single check, shown in a modal before opening the saved record.
  const [result, setResult] = useState(null);
  const isBulk = activeTab === 'bulk';

  const formik = useFormik({
    initialValues: emptyForm,
    validate: async (values) => {
      const schema = isBulk ? bulkValidationSchema : validationSchema;
      const errors = {};

      try {
        await schema.validate(values, { abortEarly: false });
      } catch (error) {
        if (!(error instanceof Yup.ValidationError)) {
          throw error;
        }

        error.inner.forEach(({ path, message }) => {
          if (path && !errors[path]) {
            errors[path] = message;
          }
        });
      }

      if (isBulk) {
        if (bulkSubscribers.length === 0 || !bulkFile) {
          errors.bulkSubscribers = 'Import an Excel file with subscriber details before submitting.';
        } else {
          if (!values.tradingPartnerServiceId && bulkSubscribers.some((row) => !row.insuranceName)) {
            errors.tradingPartnerServiceId = 'Pick a default payer for rows that have no Insurance Name.';
          }
          if (bulkSubscribers.some((row) => !row.serviceDateFrom || !row.serviceDateTo)) {
            if (!values.beginningDateOfService) errors.beginningDateOfService = 'Pick dates for rows without their own service dates.';
            if (!values.endDateOfService) errors.endDateOfService = 'Pick dates for rows without their own service dates.';
          }
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!isBulk) {
        try {
          const response = await postClaimStatus(buildPayload(values), values.organizationId, values.providerId, token);
          setResult(response?.data ?? null);
        } catch (error) {
          console.error('Failed to run claim status check:', error);
          toast.error(error.message || 'Unable to run claim status check.');
        }
        return;
      }

      try {
        const response = await postBulkClaimStatus(buildBulkForm(values, bulkFile, bulkSubscribers), values.organizationId, token);
        toast.success(response?.message || 'Bulk claim status check started. The report will be emailed to you.');
        navigate(APPRoutes.CLAIM_STATUS);
      } catch (error) {
        console.error('Failed to start bulk claim status check:', error);
        // Unrecognized insurance names are flagged on the import preview instead of in a toast.
        if (error.data?.error_type === 'UNKNOWN_INSURANCE') {
          setBulkErrors(error.data.unknown_insurances ?? []);
          return;
        }
        toast.error(error.message || 'Unable to start the bulk claim status check.');
      }
    },
  });
  const { setValues } = formik;

  const loadClaimStatus = useCallback(async () => {
    if (!isEdit) return;

    try {
      setLoading(true);
      const stateRecord = location.state?.claimStatus;
      const record = stateRecord && String(stateRecord.id) === String(id) && stateRecord.organizationId
        ? stateRecord
        : (await getClaimStatusRecord(id, token))?.data;
      if (!record) throw new Error('Claim status record was not found.');
      setValues(mapRecordToForm(record));
    } catch (error) {
      console.error('Failed to load claim status:', error);
      toast.error(error.message || 'Failed to load claim status');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, location.state, token, setValues]);

  React.useEffect(() => {
    if (!isEdit || !token) {
      setLoading(false);
      return;
    }
    loadClaimStatus();
  }, [isEdit, token, loadClaimStatus]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setBulkErrors([]);
    formik.setErrors({});
    formik.setTouched({}, false);
  };

  const beginDate = formik.values.beginningDateOfService;
  // In bulk, the form dates are only needed for rows that carry no service dates of their own.
  const datesRequired = !isBulk || bulkSubscribers.some((row) => !row.serviceDateFrom || !row.serviceDateTo);

  return (
    <StediFormShell
      title={isEdit ? 'Re-check Claim Status' : 'Check Claim Status'}
      subtitle="Runs a Stedi claim status check and saves the patient to the selected organization"
      onBack={() => navigate(-1)}
      tabs={isEdit ? null : TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      loading={loading}
      canSave={canSave}
      deniedMessage="You do not have permission to save claim status."
      onSubmit={formik.handleSubmit}
      isSubmitting={formik.isSubmitting}
      submitLabel={isBulk ? 'Submit Bulk Check' : 'Check Claim Status'}
    >
      <FormSection
        icon={MdBusiness}
        title="Organization & Payer"
        subtitle={isBulk
          ? 'Where the patients are saved and which payer to ask'
          : 'Where the patient is saved and which payer to ask; the organization is the billing provider'}
      >
        <div className="col-12 col-md-6">
          <OrganizationSelect formik={formik} token={token} />
        </div>
        {!isBulk && (
          <div className="col-12 col-md-6">
            <ProviderSelect formik={formik} token={token} label="Billing Provider NPI" />
          </div>
        )}
        <div className="col-12 col-md-6">
          <PayerSelect
            formik={formik}
            name="tradingPartnerServiceId"
            payers={payers}
            loading={payersLoading}
            label={isBulk ? 'Default Payer' : 'Insurance Payer'}
            required={!isBulk}
          />
        </div>
      </FormSection>

      {isBulk ? (
        <FormSection icon={MdGroups} title="Patients" subtitle="Checked in the background; the file is emailed back to you with the results added">
          <BulkSubscriberImport
            kind="claim-status"
            rows={bulkSubscribers}
            file={bulkFile}
            onRowsChange={(rows, file) => {
              setBulkSubscribers(rows);
              setBulkFile(file);
              setBulkErrors([]);
            }}
            defaultPayerId={formik.values.tradingPartnerServiceId}
            payers={payers}
            error={formik.submitCount > 0 ? formik.errors.bulkSubscribers : ''}
            serverErrors={bulkErrors}
          />
        </FormSection>
      ) : (
        <FormSection icon={MdPerson} title="Patient" subtitle="Matched to an existing patient by name and date of birth">
          <TextInput formik={formik} name="firstName" label="First Name" placeholder="e.g. Jane" autoComplete="off" />
          <TextInput formik={formik} name="lastName" label="Last Name" placeholder="e.g. Doe" autoComplete="off" />
          <TextInput formik={formik} name="memberId" label="Member ID" placeholder="From the insurance card" autoComplete="off" />
          <DateInput formik={formik} name="dob" label="Date of Birth" maxDate={dayjs()} />
        </FormSection>
      )}

      <FormSection
        icon={MdEventNote}
        title="Dates of Service"
        subtitle={isBulk ? 'Used for rows without their own service_date_from / service_date_to' : 'The service period the claim covers'}
      >
        <DateInput formik={formik} name="beginningDateOfService" label="From" required={datesRequired} maxDate={dayjs()} />
        <DateInput
          formik={formik}
          name="endDateOfService"
          label="To"
          required={datesRequired}
          minDate={beginDate ? dayjs(beginDate) : undefined}
          maxDate={dayjs()}
        />
      </FormSection>

      {/* In bulk, each row carries its own procedure code and billing provider. */}
      {!isBulk && (
        <FormSection
          icon={MdMedicalServices}
          title="Service Line"
          subtitle="Optional: shows the status of this procedure's line on the claim, not just the claim's"
        >
          <TextInput formik={formik} name="procedureCode" label="Procedure Code" placeholder="e.g. 90837" required={false} autoComplete="off" />
          <TextInput formik={formik} name="lineChargeAmount" label="Charge Amount" placeholder="e.g. 150.00" required={false} inputMode="decimal" />
        </FormSection>
      )}

      {/* Closing opens the saved record; the list is the fallback if it could not be saved. */}
      <ClaimStatusResultModal
        result={result}
        payerId={formik.values.tradingPartnerServiceId}
        payerName={payers.find((payer) => payer.primaryPayerId === formik.values.tradingPartnerServiceId)?.name}
        onClose={() => navigate(result?.checkId
          ? APPRoutes.CLAIM_STATUS_VIEW.replace(':id', result.checkId)
          : APPRoutes.CLAIM_STATUS)}
      />
    </StediFormShell>
  );
}
